import {
  BadgeCheck,
  Boxes,
  ClipboardList,
  FileDown,
  LayoutDashboard,
  LogOut,
  PackagePlus,
  ScrollText,
  Settings,
  Upload,
  Users,
  Wrench,
} from 'lucide-react';

import type { Permission } from '@/lib/session';

/**
 * Navigation model.
 *
 * Grouped by *whose work it is*, not by entity. The prototype had one flat list of ten
 * buttons in which "Bulk Import" sat between "Reports" and "Settings" with nothing to
 * say who it was for; here IT sees the hardware section, HR sees the people section,
 * and an employee sees one screen. Sections a role has no business in are hidden
 * rather than shown disabled.
 */

export interface NavItem {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  /** One line on what this destination is for. Shown in the sidebar tooltip when the
   *  rail is collapsed, which is the only label there is at that width. */
  description: string;
  /**
   * The permission that reveals it. Undefined means everyone signed in - used only
   * for My assets, which every employee has.
   *
   * A LIST means any one of them is enough. Bulk import is the case: IT imports assets
   * and assignments, HR imports employees, and they are peers rather than a ladder, so
   * no single permission describes who the screen is for. Picking one would have hidden
   * it from the other, and `viewRegister` - which it used to carry - showed it to every
   * viewer and consultant, for whom the page can do nothing but hand out CSV templates.
   */
  permission?: Permission | readonly Permission[];
  /** Names an entry in the badges map; the pill hides when the count is 0. */
  badgeKey?: string;
}

export interface NavSection {
  label: string;
  items: NavItem[];
}

export const NAV_SECTIONS: NavSection[] = [
  {
    label: 'Overview',
    items: [
      {
        title: 'Dashboard',
        href: '/dashboard',
        icon: LayoutDashboard,
        description: 'What is out, what is free, and what needs attention today.',
        permission: 'viewRegister',
      },
      {
        title: 'My assets',
        href: '/my-assets',
        icon: BadgeCheck,
        description:
          'The assets issued to you. Confirm you received them - that acknowledgement is what settles a question later.',
        badgeKey: 'unacknowledgedMine',
      },
    ],
  },
  {
    label: 'Hardware',
    items: [
      {
        title: 'Assets',
        href: '/assets',
        icon: Boxes,
        description:
          'The register: every asset, its code, serial, warranty and who holds it right now.',
        permission: 'viewRegister',
      },
      /*
       * Assign and Return are deliberately NOT in here.
       *
       * Both are actions on ONE asset, and the register is where you already have that
       * asset on screen - so they are row actions there and buttons on the detail
       * screen, gated on the same shared predicates the API judges the request by. A
       * sidebar entry meant finding the asset twice: once to look at it, then again in
       * a picker on a different page.
       *
       * The pages themselves still exist and are still routed. `/assign` is where the
       * requests queue sends you (prefilled with the beneficiary and the request it
       * settles) and it is the better screen for kitting out one joiner, because it
       * keeps the person and the accessories between submissions. Both are one click
       * from Quick actions on the dashboard.
       */
      {
        title: 'Requests',
        href: '/requests',
        icon: PackagePlus,
        description:
          'Hardware people have asked for. Nothing here is reserved - issuing something against a request is what closes it.',
        permission: 'viewRegister',
        badgeKey: 'openAssetRequests',
      },
      {
        title: 'Repairs',
        href: '/repairs',
        icon: Wrench,
        description: 'Repairs in progress and the full history, with vendor, cost and parts.',
        permission: 'viewRegister',
        badgeKey: 'openRepairs',
      },
    ],
  },
  {
    label: 'People',
    items: [
      {
        title: 'Employees',
        href: '/employees',
        icon: Users,
        description: 'The directory, and what each person is currently holding.',
        permission: 'viewRegister',
      },
      {
        title: 'Exit & clearance',
        href: '/exits',
        icon: LogOut,
        description:
          'Close someone out. An exit cannot be marked Cleared while they still hold company property.',
        permission: 'runExitClearance',
        badgeKey: 'exitedHoldingAssets',
      },
    ],
  },
  {
    label: 'Data',
    items: [
      {
        title: 'Bulk import',
        href: '/import',
        icon: Upload,
        description:
          'Load employees, assets and assignments from CSV. Every rejected row is reported against the line number Excel shows.',
        permission: ['manageEmployees', 'manageAssets', 'assignAssets'],
      },
      {
        title: 'Reports',
        href: '/reports',
        icon: FileDown,
        description:
          'CSV exports for Excel: the full register, who has what, repairs, warranties and exit clearance.',
        permission: 'viewReports',
      },
      /*
       * Archive is gone, and so is its page.
       *
       * It ran three queries - RETIRED assets, DISPOSED assets, EXITED employees - that
       * both registers already answer, and answered them worse: capped at 100 rows,
       * with no search, no sort and no paging. The register has a status filter and a
       * Show archived toggle; the directory has Show exited. Same rows, better tools,
       * already there:
       *
       *   /assets?status=RETIRED&includeArchived=true
       *   /employees?status=EXITED&includeExited=true
       */
    ],
  },
  {
    label: 'Administration',
    items: [
      {
        title: 'Settings',
        href: '/settings',
        icon: Settings,
        description: 'The asset code sequence, categories, and who holds which role.',
        permission: 'manageSettings',
      },
      {
        title: 'Audit log',
        href: '/audit',
        icon: ScrollText,
        description: 'Every change to an asset, an assignment or a person - who made it, and when.',
        permission: 'manageSettings',
      },
    ],
  },
];

/** Sections with their invisible items stripped, and empty sections dropped. */
export function visibleNavSections(permissions: readonly Permission[]): NavSection[] {
  return NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => {
      if (item.permission === undefined) return true;
      const needed = Array.isArray(item.permission) ? item.permission : [item.permission];
      return needed.some((p) => permissions.includes(p));
    }),
  })).filter((section) => section.items.length > 0);
}

/** A nav count pill: the number, and whether it is a queue someone must act on. */
export interface NavBadge {
  count: number;
  urgent?: boolean;
}

export type NavBadges = Partial<Record<string, NavBadge>>;

/** Longest matching href wins, so /assets/new does not also light up a sibling. */
export function isActivePath(pathname: string, href: string): boolean {
  return pathname === href || pathname.startsWith(`${href}/`);
}

/** Icons re-exported so the sidebar needs one import. */
export { ClipboardList };
